import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { api, type ApiResponse } from "~/lib/api";
import { ACTION_LIST_CAP, buildAttentionHeadline } from "../story";
import { ed, tbl } from "~/components/editorial";
import { fmtNum } from "~/lib/format";

interface ActionItem {
  id: string;
  title: string;
  number: number;
  author: string;
  repository: string;
  url: string;
}

interface StalePR extends ActionItem {
  daysSinceUpdate: number;
}

interface AwaitingReviewPR extends ActionItem {
  daysSinceCreated: number;
}

interface LargePR extends ActionItem {
  additions: number;
  deletions: number;
}

interface ActionItemsData {
  stalePrs: StalePR[];
  awaitingReview: AwaitingReviewPR[];
  largePrs: LargePR[];
  counts: {
    stale: number;
    awaitingReview: number;
    large: number;
  };
}

/** Open PRs that need someone, as three dense tables. */
export function ActionItems({ label }: { label: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["action-items"],
    queryFn: () =>
      api.get<ApiResponse<ActionItemsData>>("/metrics/action-items"),
    staleTime: 5 * 60 * 1000,
  });

  const items = data?.data;

  return (
    <section>
      <div className={ed.sect}>{label}</div>
      {isLoading ? (
        <div className={ed.loadingSm}>Checking open PRs…</div>
      ) : !items ? (
        <p className={ed.empty}>Open PRs didn't load.</p>
      ) : (
        <>
          <h2 className={ed.h2}>
            {buildAttentionHeadline({
              stale: items.counts.stale,
              awaitingReview: items.counts.awaitingReview,
              large: items.counts.large,
            })}
          </h2>
          <div className={ed.halves}>
            <PrTable
              title="Gone quiet"
              sub="Open, no activity for 7+ days. Oldest first."
              rows={items.stalePrs.slice(0, 5).map((pr) => ({
                ...pr,
                value: `${fmtNum(pr.daysSinceUpdate)}d`,
              }))}
              valueLabel="Idle"
              total={items.counts.stale}
            />
            <PrTable
              title="Waiting on a first review"
              sub="Open a day or more with no review. Oldest first."
              rows={items.awaitingReview.slice(0, 5).map((pr) => ({
                ...pr,
                value: `${fmtNum(pr.daysSinceCreated)}d`,
              }))}
              valueLabel="Open"
              total={items.counts.awaitingReview}
            />
          </div>
          {items.largePrs.length > 0 && (
            <div className={styles.spaced}>
              <PrTable
                title="Too large to review well"
                sub="Open, more than 500 added lines. Largest first."
                rows={items.largePrs.slice(0, 3).map((pr) => ({
                  ...pr,
                  value: `+${fmtNum(pr.additions)} / -${fmtNum(pr.deletions)}`,
                }))}
                valueLabel="Lines"
                total={items.counts.large}
              />
            </div>
          )}
          <p className={ed.method}>
            Open PRs across every connected repo, as of now rather than the
            selected period. A PR can appear in more than one list. Each list
            stops at {ACTION_LIST_CAP} (5 for large PRs).
          </p>
        </>
      )}
    </section>
  );
}

function PrTable({
  title,
  sub,
  rows,
  valueLabel,
  total,
}: {
  title: string;
  sub: string;
  rows: Array<ActionItem & { value: string }>;
  valueLabel: string;
  total: number;
}) {
  return (
    <div>
      <h3 className={ed.h3}>
        {title} <span className={`${ed.num} ${ed.muted}`}>{fmtNum(total)}</span>
      </h3>
      <p className={styles.sub}>{sub}</p>
      {rows.length === 0 ? (
        <p className={ed.empty}>None.</p>
      ) : (
        <div className={tbl.scrollPlain}><table className={tbl.simple}>
          <thead>
            <tr>
              <th>PR</th>
              <th>Author</th>
              <th data-num>{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((pr) => (
              <tr key={pr.id}>
                <td className={tbl.clipCell}>
                  <a href={pr.url} target="_blank" rel="noopener noreferrer" title={pr.title}>
                    {pr.title}
                  </a>
                  <div className={styles.repo}>
                    {pr.repository} <span className={ed.num}>#{pr.number}</span>
                  </div>
                </td>
                <td className={styles.author}>{pr.author}</td>
                <td data-num>{pr.value}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

const styles = {
  spaced: css({ mt: "24px" }),
  sub: css({
    fontFamily: "sans",
    fontSize: "12.5px",
    color: "ed.ink2",
    m: 0,
    mb: "8px",
  }),
  repo: css({
    fontFamily: "mono",
    fontSize: "11px",
    color: "ed.ink3",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  author: css({ whiteSpace: "nowrap", color: "ed.ink2" }),
};
